import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Shield, TrendingUp, TrendingDown, DollarSign, AlertTriangle,
  Activity, BarChart3, RefreshCw, PieChart, ArrowUpRight,
  ArrowDownRight, Percent, Target, Zap, ChevronDown, ChevronUp,
} from 'lucide-react';
import { brokerAPI, BrokerAccount, BrokerPosition, PortfolioHistory } from '../../api/broker';

/* ────────────────────────────────────────────────── helpers */

const fmt = (n: number, decimals = 2) =>
  n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const fmtUSD = (n: number, decimals = 2) =>
  `$${fmt(Math.abs(n), decimals)}`;

const fmtPct = (n: number) =>
  `${n >= 0 ? '+' : ''}${fmt(n, 2)}%`;

const pf = (s: string | undefined | null) => parseFloat(s || '0') || 0;

type RiskLevel = 'low' | 'medium' | 'high' | 'critical';

function getRiskLevel(pctOfPortfolio: number, unrealizedPlPct: number): RiskLevel {
  if (pctOfPortfolio > 30 || unrealizedPlPct < -15) return 'critical';
  if (pctOfPortfolio > 20 || unrealizedPlPct < -10) return 'high';
  if (pctOfPortfolio > 10 || unrealizedPlPct < -5) return 'medium';
  return 'low';
}

const riskBadgeClasses: Record<RiskLevel, string> = {
  low: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  medium: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  high: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  critical: 'bg-red-500/20 text-red-400 border-red-500/30',
};

const POSITION_COLORS = [
  '#6366f1', '#8b5cf6', '#a78bfa', '#818cf8',
  '#38bdf8', '#22d3ee', '#2dd4bf', '#34d399',
  '#4ade80', '#a3e635', '#facc15', '#fb923c',
  '#f87171', '#e879f9', '#c084fc', '#f472b6',
];

/* Sector mapping heuristic — maps common symbols to sectors */
const SECTOR_MAP: Record<string, string> = {
  AAPL: 'Technology', MSFT: 'Technology', GOOGL: 'Technology', GOOG: 'Technology',
  AMZN: 'Consumer Cyclical', TSLA: 'Consumer Cyclical', META: 'Technology',
  NVDA: 'Technology', AMD: 'Technology', INTC: 'Technology', AVGO: 'Technology',
  JPM: 'Financial', BAC: 'Financial', GS: 'Financial', MS: 'Financial', WFC: 'Financial',
  JNJ: 'Healthcare', UNH: 'Healthcare', PFE: 'Healthcare', ABBV: 'Healthcare',
  XOM: 'Energy', CVX: 'Energy', COP: 'Energy', SLB: 'Energy',
  PG: 'Consumer Defensive', KO: 'Consumer Defensive', PEP: 'Consumer Defensive',
  DIS: 'Communication', NFLX: 'Communication', CMCSA: 'Communication',
  SPY: 'ETF', QQQ: 'ETF', IWM: 'ETF', DIA: 'ETF', VTI: 'ETF', VOO: 'ETF',
  BTC: 'Crypto', ETH: 'Crypto', SOL: 'Crypto', BTCUSD: 'Crypto', ETHUSD: 'Crypto',
};

function getSector(symbol: string, assetClass: string): string {
  if (assetClass === 'crypto' || symbol.endsWith('USD')) return 'Crypto';
  if (SECTOR_MAP[symbol]) return SECTOR_MAP[symbol];
  return 'Other';
}

/* ────────────────────────────────────────────────── component */

const RiskDashboard: React.FC = () => {
  const [account, setAccount] = useState<BrokerAccount | null>(null);
  const [positions, setPositions] = useState<BrokerPosition[]>([]);
  const [history, setHistory] = useState<PortfolioHistory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [sortCol, setSortCol] = useState<'value' | 'pct' | 'pl' | 'risk'>('value');
  const [sortAsc, setSortAsc] = useState(false);
  const [expandedSectors, setExpandedSectors] = useState<Set<string>>(new Set());

  /* ── data fetching ────────────────────────────── */

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [acct, pos, hist] = await Promise.all([
        brokerAPI.getAccount(),
        brokerAPI.getPositions(),
        brokerAPI.getPortfolioHistory('3M', '1D'),
      ]);
      setAccount(acct);
      setPositions(pos.positions);
      setHistory(hist);
      setLastRefresh(new Date());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ── derived metrics ──────────────────────────── */

  const metrics = useMemo(() => {
    if (!account || !history) return null;

    const portfolioValue = pf(account.portfolio_value);
    const cash = pf(account.cash);
    const equity = pf(account.equity);
    const lastEquity = pf(account.last_equity);
    const buyingPower = pf(account.buying_power);
    const longExposure = pf(account.long_market_value);
    const shortExposure = Math.abs(pf(account.short_market_value));
    const netExposure = longExposure - shortExposure;
    const cashPct = portfolioValue > 0 ? (cash / portfolioValue) * 100 : 0;

    const dayPL = equity - lastEquity;
    const dayPLPct = lastEquity > 0 ? (dayPL / lastEquity) * 100 : 0;

    const totalUnrealizedPL = positions.reduce((s, p) => s + pf(p.unrealized_pl), 0);
    const totalCostBasis = positions.reduce((s, p) => s + pf(p.cost_basis), 0);
    const totalPLPct = totalCostBasis > 0 ? (totalUnrealizedPL / totalCostBasis) * 100 : 0;

    // Max drawdown from equity curve
    const equityCurve = history.equity;
    let maxDD = 0;
    let peak = equityCurve[0] || 0;
    for (const val of equityCurve) {
      if (val > peak) peak = val;
      const dd = peak > 0 ? ((peak - val) / peak) * 100 : 0;
      if (dd > maxDD) maxDD = dd;
    }

    // Daily returns for Sharpe / VaR
    const dailyReturns: number[] = [];
    for (let i = 1; i < equityCurve.length; i++) {
      if (equityCurve[i - 1] > 0) {
        dailyReturns.push((equityCurve[i] - equityCurve[i - 1]) / equityCurve[i - 1]);
      }
    }

    const meanReturn = dailyReturns.length > 0
      ? dailyReturns.reduce((a, b) => a + b, 0) / dailyReturns.length : 0;
    const variance = dailyReturns.length > 1
      ? dailyReturns.reduce((s, r) => s + (r - meanReturn) ** 2, 0) / (dailyReturns.length - 1) : 0;
    const stdDev = Math.sqrt(variance);
    const sharpeRatio = stdDev > 0 ? (meanReturn / stdDev) * Math.sqrt(252) : 0;

    // Win rate
    const wins = dailyReturns.filter(r => r > 0).length;
    const winRate = dailyReturns.length > 0 ? (wins / dailyReturns.length) * 100 : 0;

    // VaR (95% confidence, 2-sigma)
    const dailyVaR = stdDev * 1.645 * portfolioValue;
    // Also compute 99% VaR
    const dailyVaR99 = stdDev * 2.326 * portfolioValue;

    // Position-level analytics
    const positionData = positions.map((p, idx) => {
      const mv = pf(p.market_value);
      const pctPortfolio = portfolioValue > 0 ? (mv / portfolioValue) * 100 : 0;
      const upl = pf(p.unrealized_pl);
      const uplPct = pf(p.unrealized_plpc) * 100;
      const sector = getSector(p.symbol, p.asset_class);
      const risk = getRiskLevel(pctPortfolio, uplPct);
      return {
        symbol: p.symbol,
        qty: pf(p.qty),
        side: p.side,
        marketValue: mv,
        costBasis: pf(p.cost_basis),
        currentPrice: pf(p.current_price),
        avgEntry: pf(p.avg_entry_price),
        pctPortfolio,
        unrealizedPL: upl,
        unrealizedPLPct: uplPct,
        changeToday: pf(p.change_today) * 100,
        sector,
        assetClass: p.asset_class,
        riskLevel: risk,
        color: POSITION_COLORS[idx % POSITION_COLORS.length],
      };
    });

    // Largest position
    const largestPos = positionData.reduce(
      (max, p) => (p.pctPortfolio > max.pctPortfolio ? p : max),
      { symbol: 'N/A', pctPortfolio: 0 } as (typeof positionData)[0]
    );

    // Concentration warnings
    const concentrationWarnings = positionData.filter(p => p.pctPortfolio > 20);

    // Sector allocation
    const sectorAlloc = new Map<string, { value: number; pct: number; positions: typeof positionData }>();
    for (const p of positionData) {
      const existing = sectorAlloc.get(p.sector) || { value: 0, pct: 0, positions: [] };
      existing.value += p.marketValue;
      existing.pct += p.pctPortfolio;
      existing.positions.push(p);
      sectorAlloc.set(p.sector, existing);
    }

    // Risk score (1-100): composite
    let riskScore = 50; // baseline
    riskScore += maxDD > 20 ? 20 : maxDD > 10 ? 10 : maxDD > 5 ? 5 : 0;
    riskScore += (largestPos?.pctPortfolio ?? 0) > 30 ? 15 : (largestPos?.pctPortfolio ?? 0) > 20 ? 10 : 0;
    riskScore -= sharpeRatio > 1.5 ? 15 : sharpeRatio > 1 ? 10 : sharpeRatio > 0.5 ? 5 : 0;
    riskScore += cashPct < 5 ? 10 : cashPct < 10 ? 5 : 0;
    riskScore -= winRate > 60 ? 10 : winRate > 50 ? 5 : 0;
    riskScore = Math.max(1, Math.min(100, riskScore));

    return {
      portfolioValue, cash, equity, lastEquity, buyingPower,
      longExposure, shortExposure, netExposure, cashPct,
      dayPL, dayPLPct, totalUnrealizedPL, totalPLPct,
      maxDD, sharpeRatio, winRate,
      dailyVaR, dailyVaR99, stdDev,
      positionData, largestPos, concentrationWarnings,
      sectorAlloc, riskScore,
      equityCurve, timestamps: history.timestamps,
    };
  }, [account, positions, history]);

  /* ── sorting ──────────────────────────────────── */

  const sortedPositions = useMemo(() => {
    if (!metrics) return [];
    const sorted = [...metrics.positionData];
    sorted.sort((a, b) => {
      let cmp = 0;
      switch (sortCol) {
        case 'value': cmp = a.marketValue - b.marketValue; break;
        case 'pct': cmp = a.pctPortfolio - b.pctPortfolio; break;
        case 'pl': cmp = a.unrealizedPL - b.unrealizedPL; break;
        case 'risk': {
          const order: Record<RiskLevel, number> = { low: 0, medium: 1, high: 2, critical: 3 };
          cmp = order[a.riskLevel] - order[b.riskLevel]; break;
        }
      }
      return sortAsc ? cmp : -cmp;
    });
    return sorted;
  }, [metrics, sortCol, sortAsc]);

  const toggleSort = (col: typeof sortCol) => {
    if (sortCol === col) setSortAsc(!sortAsc);
    else { setSortCol(col); setSortAsc(false); }
  };

  const toggleSector = (sector: string) => {
    setExpandedSectors(prev => {
      const next = new Set(prev);
      if (next.has(sector)) next.delete(sector); else next.add(sector);
      return next;
    });
  };

  /* ── SVG drawdown chart builder ───────────────── */

  const drawdownChart = useMemo(() => {
    if (!metrics || metrics.equityCurve.length < 2) return null;

    const eq = metrics.equityCurve;
    const W = 720;
    const H = 200;
    const pad = { top: 20, right: 20, bottom: 30, left: 60 };
    const cw = W - pad.left - pad.right;
    const ch = H - pad.top - pad.bottom;

    const minEq = Math.min(...eq);
    const maxEq = Math.max(...eq);
    const range = maxEq - minEq || 1;

    // Build equity path
    const points = eq.map((v, i) => {
      const x = pad.left + (i / (eq.length - 1)) * cw;
      const y = pad.top + ch - ((v - minEq) / range) * ch;
      return { x, y };
    });

    const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');

    // Build drawdown fill
    let peak = eq[0];
    const ddPoints = eq.map((v, i) => {
      if (v > peak) peak = v;
      const dd = peak > 0 ? (peak - v) / peak : 0;
      const x = pad.left + (i / (eq.length - 1)) * cw;
      const y = pad.top + dd * ch * (100 / (metrics.maxDD || 1));
      return { x, y: Math.min(y, pad.top + ch) };
    });
    const ddPathD = `M${pad.left},${pad.top} ` +
      ddPoints.map(p => `L${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ') +
      ` L${pad.left + cw},${pad.top} Z`;

    // Y-axis labels
    const yLabels = Array.from({ length: 5 }, (_, i) => {
      const val = minEq + (range * i) / 4;
      const y = pad.top + ch - (i / 4) * ch;
      return { val, y };
    });

    // X-axis labels (timestamps)
    const ts = metrics.timestamps;
    const xLabelCount = Math.min(5, ts.length);
    const xLabels = Array.from({ length: xLabelCount }, (_, i) => {
      const idx = Math.floor((i / (xLabelCount - 1)) * (ts.length - 1));
      const date = new Date(ts[idx] * 1000);
      const label = `${date.getMonth() + 1}/${date.getDate()}`;
      const x = pad.left + (idx / (ts.length - 1)) * cw;
      return { label, x };
    });

    return { W, H, pad, pathD, ddPathD, yLabels, xLabels };
  }, [metrics]);

  /* ── risk score color ─────────────────────────── */

  function riskScoreColor(score: number): string {
    if (score <= 25) return '#22c55e';
    if (score <= 50) return '#eab308';
    if (score <= 75) return '#f97316';
    return '#ef4444';
  }

  function riskScoreLabel(score: number): string {
    if (score <= 25) return 'Low';
    if (score <= 50) return 'Moderate';
    if (score <= 75) return 'Elevated';
    return 'High';
  }

  /* ── render ───────────────────────────────────── */

  if (loading && !account) {
    return (
      <div className="min-h-[600px] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
          <span className="text-slate-400 text-sm">Loading risk analytics...</span>
        </div>
      </div>
    );
  }

  if (error && !account) {
    return (
      <div className="min-h-[400px] flex items-center justify-center">
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-3" />
          <h3 className="text-red-300 font-semibold mb-1">Failed to Load Risk Data</h3>
          <p className="text-red-400/70 text-sm mb-4">{error}</p>
          <button
            onClick={fetchData}
            className="px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-300 rounded-lg text-sm transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!metrics) return null;

  const m = metrics;

  return (
    <div className="space-y-6">
      {/* ── Header ──────────────────────────────── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2.5 bg-indigo-500/20 rounded-xl">
            <Shield className="w-6 h-6 text-indigo-400" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Risk Dashboard</h2>
            <p className="text-slate-400 text-sm">Portfolio risk analytics and exposure monitoring</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">
            Updated {lastRefresh.toLocaleTimeString()}
          </span>
          <button
            onClick={fetchData}
            disabled={loading}
            className="p-2 bg-slate-800 hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 text-slate-400 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* ── Risk Score Banner ───────────────────── */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="relative w-20 h-20">
              <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
                <circle cx="50" cy="50" r="42" fill="none" stroke="#334155" strokeWidth="8" />
                <circle
                  cx="50" cy="50" r="42" fill="none"
                  stroke={riskScoreColor(m.riskScore)}
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${(m.riskScore / 100) * 264} 264`}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-xl font-bold text-white">{m.riskScore}</span>
              </div>
            </div>
            <div>
              <div className="text-sm text-slate-400 mb-0.5">Overall Risk Score</div>
              <div className="text-lg font-semibold" style={{ color: riskScoreColor(m.riskScore) }}>
                {riskScoreLabel(m.riskScore)}
              </div>
              <div className="text-xs text-slate-500 mt-1">
                Based on drawdown, concentration, volatility, and win rate
              </div>
            </div>
          </div>

          {m.concentrationWarnings.length > 0 && (
            <div className="bg-orange-500/10 border border-orange-500/30 rounded-lg px-4 py-3">
              <div className="flex items-center gap-2 text-orange-400 text-sm font-medium mb-1">
                <AlertTriangle className="w-4 h-4" />
                Concentration Warning
              </div>
              {m.concentrationWarnings.map(w => (
                <div key={w.symbol} className="text-orange-300/70 text-xs">
                  {w.symbol} is {fmt(w.pctPortfolio, 1)}% of portfolio
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Stat Cards Row 1 ────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Portfolio Value"
          value={fmtUSD(m.portfolioValue)}
          icon={<DollarSign className="w-4 h-4" />}
          iconBg="bg-indigo-500/20"
          iconColor="text-indigo-400"
        />
        <StatCard
          label="Day P&L"
          value={`${m.dayPL >= 0 ? '+' : '-'}${fmtUSD(m.dayPL)}`}
          sub={fmtPct(m.dayPLPct)}
          positive={m.dayPL >= 0}
          icon={m.dayPL >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
          iconBg={m.dayPL >= 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'}
          iconColor={m.dayPL >= 0 ? 'text-emerald-400' : 'text-red-400'}
        />
        <StatCard
          label="Total P&L"
          value={`${m.totalUnrealizedPL >= 0 ? '+' : '-'}${fmtUSD(m.totalUnrealizedPL)}`}
          sub={fmtPct(m.totalPLPct)}
          positive={m.totalUnrealizedPL >= 0}
          icon={<Activity className="w-4 h-4" />}
          iconBg={m.totalUnrealizedPL >= 0 ? 'bg-emerald-500/20' : 'bg-red-500/20'}
          iconColor={m.totalUnrealizedPL >= 0 ? 'text-emerald-400' : 'text-red-400'}
        />
        <StatCard
          label="Buying Power"
          value={fmtUSD(m.buyingPower)}
          icon={<Zap className="w-4 h-4" />}
          iconBg="bg-cyan-500/20"
          iconColor="text-cyan-400"
        />
      </div>

      {/* ── Stat Cards Row 2 ────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard
          label="Max Drawdown"
          value={`-${fmt(m.maxDD, 2)}%`}
          icon={<TrendingDown className="w-4 h-4" />}
          iconBg="bg-red-500/20"
          iconColor="text-red-400"
          valueColor="text-red-400"
        />
        <StatCard
          label="Sharpe Ratio"
          value={fmt(m.sharpeRatio, 2)}
          sub="Annualized"
          icon={<Target className="w-4 h-4" />}
          iconBg={m.sharpeRatio >= 1 ? 'bg-emerald-500/20' : 'bg-yellow-500/20'}
          iconColor={m.sharpeRatio >= 1 ? 'text-emerald-400' : 'text-yellow-400'}
          valueColor={m.sharpeRatio >= 1 ? 'text-emerald-400' : 'text-yellow-400'}
        />
        <StatCard
          label="Win Rate"
          value={`${fmt(m.winRate, 1)}%`}
          sub={`${positions.length} positions`}
          icon={<Percent className="w-4 h-4" />}
          iconBg={m.winRate >= 50 ? 'bg-emerald-500/20' : 'bg-red-500/20'}
          iconColor={m.winRate >= 50 ? 'text-emerald-400' : 'text-red-400'}
          valueColor={m.winRate >= 50 ? 'text-emerald-400' : 'text-red-400'}
        />
      </div>

      {/* ── Main Grid: Allocation + Exposure ────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* ── Portfolio Allocation ─────────────── */}
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <PieChart className="w-4 h-4 text-indigo-400" />
            <h3 className="text-white font-semibold text-sm">Portfolio Allocation</h3>
          </div>

          {/* Horizontal stacked bar */}
          <div className="h-8 rounded-lg overflow-hidden flex mb-4">
            {m.positionData.map((p) => (
              <div
                key={p.symbol}
                className="h-full relative group transition-opacity hover:opacity-80"
                style={{ width: `${Math.max(p.pctPortfolio, 0.5)}%`, backgroundColor: p.color }}
                title={`${p.symbol}: ${fmt(p.pctPortfolio, 1)}%`}
              >
                {p.pctPortfolio > 8 && (
                  <span className="absolute inset-0 flex items-center justify-center text-xs font-medium text-white/90 truncate px-1">
                    {p.symbol}
                  </span>
                )}
              </div>
            ))}
            {m.cashPct > 0 && (
              <div
                className="h-full bg-slate-600"
                style={{ width: `${m.cashPct}%` }}
                title={`Cash: ${fmt(m.cashPct, 1)}%`}
              />
            )}
          </div>

          {/* Legend */}
          <div className="grid grid-cols-2 gap-1.5 mb-5">
            {m.positionData.map(p => (
              <div key={p.symbol} className="flex items-center gap-2 text-xs">
                <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: p.color }} />
                <span className="text-slate-300 truncate">{p.symbol}</span>
                <span className="text-slate-500 ml-auto">{fmt(p.pctPortfolio, 1)}%</span>
              </div>
            ))}
            {m.cashPct > 0 && (
              <div className="flex items-center gap-2 text-xs">
                <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0 bg-slate-600" />
                <span className="text-slate-300">Cash</span>
                <span className="text-slate-500 ml-auto">{fmt(m.cashPct, 1)}%</span>
              </div>
            )}
          </div>

          {/* By Sector */}
          <div className="border-t border-slate-700/50 pt-4">
            <h4 className="text-xs text-slate-400 font-medium uppercase tracking-wider mb-3">By Sector</h4>
            <div className="space-y-2">
              {Array.from(m.sectorAlloc.entries())
                .sort((a, b) => b[1].pct - a[1].pct)
                .map(([sector, data]) => (
                  <div key={sector}>
                    <button
                      onClick={() => toggleSector(sector)}
                      className="w-full flex items-center justify-between text-xs group"
                    >
                      <span className="text-slate-300 group-hover:text-white transition-colors">{sector}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-slate-400">{fmtUSD(data.value, 0)}</span>
                        <span className="text-slate-500">{fmt(data.pct, 1)}%</span>
                        {expandedSectors.has(sector)
                          ? <ChevronUp className="w-3 h-3 text-slate-500" />
                          : <ChevronDown className="w-3 h-3 text-slate-500" />}
                      </div>
                    </button>
                    <div className="h-1.5 bg-slate-700 rounded-full mt-1 overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full transition-all"
                        style={{ width: `${data.pct}%` }}
                      />
                    </div>
                    {expandedSectors.has(sector) && (
                      <div className="mt-1 ml-3 space-y-0.5">
                        {data.positions.map(p => (
                          <div key={p.symbol} className="flex items-center justify-between text-xs text-slate-500">
                            <span>{p.symbol}</span>
                            <span>{fmt(p.pctPortfolio, 1)}%</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
            </div>
          </div>
        </div>

        {/* ── Exposure & VaR ──────────────────── */}
        <div className="space-y-6">
          {/* Exposure Summary */}
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <BarChart3 className="w-4 h-4 text-cyan-400" />
              <h3 className="text-white font-semibold text-sm">Exposure Summary</h3>
            </div>
            <div className="space-y-3">
              <ExposureRow label="Long Exposure" value={m.longExposure} total={m.portfolioValue} color="emerald" />
              <ExposureRow label="Short Exposure" value={m.shortExposure} total={m.portfolioValue} color="red" />
              <ExposureRow label="Net Exposure" value={m.netExposure} total={m.portfolioValue} color="indigo" />
              <div className="border-t border-slate-700/50 pt-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-400">Cash</span>
                  <div className="flex items-center gap-3">
                    <span className="text-white font-medium">{fmtUSD(m.cash)}</span>
                    <span className="text-slate-500">{fmt(m.cashPct, 1)}%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Concentration Risk */}
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <AlertTriangle className="w-4 h-4 text-orange-400" />
              <h3 className="text-white font-semibold text-sm">Concentration Risk</h3>
            </div>
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-400">Largest Position</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{m.largestPos?.symbol ?? 'N/A'}</span>
                  <span className={`text-sm font-medium ${
                    (m.largestPos?.pctPortfolio ?? 0) > 20 ? 'text-red-400' : 'text-slate-300'
                  }`}>
                    {fmt(m.largestPos?.pctPortfolio ?? 0, 1)}%
                  </span>
                </div>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    (m.largestPos?.pctPortfolio ?? 0) > 20 ? 'bg-red-500' : 'bg-indigo-500'
                  }`}
                  style={{ width: `${Math.min(m.largestPos?.pctPortfolio ?? 0, 100)}%` }}
                />
              </div>
              <div className="flex items-center justify-between text-xs text-slate-500">
                <span>0%</span>
                <span className="text-orange-400">20% threshold</span>
                <span>100%</span>
              </div>
              {positions.length > 0 && (
                <div className="text-xs text-slate-500">
                  {positions.length} position{positions.length !== 1 ? 's' : ''} across{' '}
                  {m.sectorAlloc.size} sector{m.sectorAlloc.size !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          </div>

          {/* Daily VaR */}
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="w-4 h-4 text-purple-400" />
              <h3 className="text-white font-semibold text-sm">Value at Risk (VaR)</h3>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-700/30 rounded-lg p-3">
                <div className="text-xs text-slate-400 mb-1">95% Daily VaR</div>
                <div className="text-lg font-bold text-orange-400">{fmtUSD(m.dailyVaR)}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {m.portfolioValue > 0 ? fmt((m.dailyVaR / m.portfolioValue) * 100, 2) : '0.00'}% of portfolio
                </div>
              </div>
              <div className="bg-slate-700/30 rounded-lg p-3">
                <div className="text-xs text-slate-400 mb-1">99% Daily VaR</div>
                <div className="text-lg font-bold text-red-400">{fmtUSD(m.dailyVaR99)}</div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {m.portfolioValue > 0 ? fmt((m.dailyVaR99 / m.portfolioValue) * 100, 2) : '0.00'}% of portfolio
                </div>
              </div>
            </div>
            <div className="mt-3 text-xs text-slate-500">
              Based on {m.equityCurve.length - 1}-day return history, daily volatility: {fmt(m.stdDev * 100, 3)}%
            </div>
          </div>
        </div>
      </div>

      {/* ── Drawdown Chart ──────────────────────── */}
      {drawdownChart && (
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <TrendingDown className="w-4 h-4 text-red-400" />
            <h3 className="text-white font-semibold text-sm">Equity Curve & Drawdown</h3>
          </div>
          <div className="overflow-x-auto">
            <svg
              viewBox={`0 0 ${drawdownChart.W} ${drawdownChart.H}`}
              className="w-full min-w-[500px]"
              preserveAspectRatio="xMidYMid meet"
            >
              {/* Grid lines */}
              {drawdownChart.yLabels.map((yl, i) => (
                <g key={i}>
                  <line
                    x1={drawdownChart.pad.left}
                    y1={yl.y}
                    x2={drawdownChart.W - drawdownChart.pad.right}
                    y2={yl.y}
                    stroke="#334155"
                    strokeWidth="0.5"
                    strokeDasharray="4 4"
                  />
                  <text
                    x={drawdownChart.pad.left - 8}
                    y={yl.y + 4}
                    textAnchor="end"
                    className="fill-slate-500"
                    fontSize="10"
                  >
                    ${(yl.val / 1000).toFixed(1)}k
                  </text>
                </g>
              ))}

              {/* X-axis labels */}
              {drawdownChart.xLabels.map((xl, i) => (
                <text
                  key={i}
                  x={xl.x}
                  y={drawdownChart.H - 6}
                  textAnchor="middle"
                  className="fill-slate-500"
                  fontSize="10"
                >
                  {xl.label}
                </text>
              ))}

              {/* Drawdown fill */}
              <path d={drawdownChart.ddPathD} fill="#ef444420" />

              {/* Equity line gradient */}
              <defs>
                <linearGradient id="eqGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity="0.3" />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* Area under equity */}
              <path
                d={`${drawdownChart.pathD} L${drawdownChart.W - drawdownChart.pad.right},${drawdownChart.pad.top + (drawdownChart.H - drawdownChart.pad.top - drawdownChart.pad.bottom)} L${drawdownChart.pad.left},${drawdownChart.pad.top + (drawdownChart.H - drawdownChart.pad.top - drawdownChart.pad.bottom)} Z`}
                fill="url(#eqGrad)"
              />

              {/* Equity line */}
              <path d={drawdownChart.pathD} fill="none" stroke="#6366f1" strokeWidth="2" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-0.5 bg-indigo-500 rounded" />
              <span>Equity Curve</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 bg-red-500/20 rounded-sm" />
              <span>Drawdown Area</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Position Risk Table ─────────────────── */}
      <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl overflow-hidden">
        <div className="p-5 pb-3 flex items-center gap-2">
          <Shield className="w-4 h-4 text-indigo-400" />
          <h3 className="text-white font-semibold text-sm">Position-Level Risk</h3>
          <span className="text-xs text-slate-500 ml-auto">{positions.length} positions</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-t border-b border-slate-700/50">
                <th className="text-left text-xs text-slate-400 font-medium px-5 py-2.5">Symbol</th>
                <th className="text-left text-xs text-slate-400 font-medium px-3 py-2.5">Side</th>
                <th className="text-left text-xs text-slate-400 font-medium px-3 py-2.5">Qty</th>
                <th className="text-left text-xs text-slate-400 font-medium px-3 py-2.5">Price</th>
                <th
                  className="text-right text-xs text-slate-400 font-medium px-3 py-2.5 cursor-pointer hover:text-white transition-colors select-none"
                  onClick={() => toggleSort('value')}
                >
                  Value {sortCol === 'value' ? (sortAsc ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />) : null}
                </th>
                <th
                  className="text-right text-xs text-slate-400 font-medium px-3 py-2.5 cursor-pointer hover:text-white transition-colors select-none"
                  onClick={() => toggleSort('pct')}
                >
                  % Portfolio {sortCol === 'pct' ? (sortAsc ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />) : null}
                </th>
                <th
                  className="text-right text-xs text-slate-400 font-medium px-3 py-2.5 cursor-pointer hover:text-white transition-colors select-none"
                  onClick={() => toggleSort('pl')}
                >
                  Unrealized P&L {sortCol === 'pl' ? (sortAsc ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />) : null}
                </th>
                <th
                  className="text-center text-xs text-slate-400 font-medium px-5 py-2.5 cursor-pointer hover:text-white transition-colors select-none"
                  onClick={() => toggleSort('risk')}
                >
                  Risk {sortCol === 'risk' ? (sortAsc ? <ChevronUp className="w-3 h-3 inline" /> : <ChevronDown className="w-3 h-3 inline" />) : null}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedPositions.map((p) => (
                <tr key={p.symbol} className="border-b border-slate-700/30 hover:bg-slate-700/20 transition-colors">
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-2">
                      <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: p.color }} />
                      <span className="text-white font-medium">{p.symbol}</span>
                      <span className="text-xs text-slate-500">{p.sector}</span>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-xs font-medium uppercase ${
                      p.side === 'long' ? 'text-emerald-400' : 'text-red-400'
                    }`}>
                      {p.side}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-slate-300">{fmt(p.qty, p.qty % 1 === 0 ? 0 : 4)}</td>
                  <td className="px-3 py-3 text-slate-300">${fmt(p.currentPrice)}</td>
                  <td className="px-3 py-3 text-right text-slate-200 font-medium">{fmtUSD(p.marketValue)}</td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <div className="w-16 h-1.5 bg-slate-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-indigo-500 rounded-full"
                          style={{ width: `${Math.min(p.pctPortfolio, 100)}%` }}
                        />
                      </div>
                      <span className="text-slate-300 text-xs w-12 text-right">{fmt(p.pctPortfolio, 1)}%</span>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {p.unrealizedPL >= 0
                        ? <ArrowUpRight className="w-3.5 h-3.5 text-emerald-400" />
                        : <ArrowDownRight className="w-3.5 h-3.5 text-red-400" />}
                      <span className={p.unrealizedPL >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                        {p.unrealizedPL >= 0 ? '+' : '-'}{fmtUSD(p.unrealizedPL)}
                      </span>
                      <span className="text-xs text-slate-500 ml-1">
                        ({fmtPct(p.unrealizedPLPct)})
                      </span>
                    </div>
                  </td>
                  <td className="px-5 py-3 text-center">
                    <span className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium border ${riskBadgeClasses[p.riskLevel]}`}>
                      {p.riskLevel.charAt(0).toUpperCase() + p.riskLevel.slice(1)}
                    </span>
                  </td>
                </tr>
              ))}
              {sortedPositions.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-5 py-8 text-center text-slate-500 text-sm">
                    No open positions
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

/* ────────────────────────────────────────────────── sub-components */

interface StatCardProps {
  label: string;
  value: string;
  sub?: string;
  positive?: boolean;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
  valueColor?: string;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, sub, positive, icon, iconBg, iconColor, valueColor }) => (
  <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4">
    <div className="flex items-center gap-2 mb-2">
      <div className={`p-1.5 rounded-lg ${iconBg}`}>
        <span className={iconColor}>{icon}</span>
      </div>
      <span className="text-xs text-slate-400">{label}</span>
    </div>
    <div className={`text-lg font-bold ${valueColor || 'text-white'}`}>{value}</div>
    {sub && (
      <div className={`text-xs mt-0.5 ${
        positive === undefined ? 'text-slate-500'
          : positive ? 'text-emerald-400' : 'text-red-400'
      }`}>
        {sub}
      </div>
    )}
  </div>
);

interface ExposureRowProps {
  label: string;
  value: number;
  total: number;
  color: string;
}

const ExposureRow: React.FC<ExposureRowProps> = ({ label, value, total, color }) => {
  const pct = total > 0 ? (value / total) * 100 : 0;
  const colorMap: Record<string, { bar: string; text: string }> = {
    emerald: { bar: 'bg-emerald-500', text: 'text-emerald-400' },
    red: { bar: 'bg-red-500', text: 'text-red-400' },
    indigo: { bar: 'bg-indigo-500', text: 'text-indigo-400' },
  };
  const c = colorMap[color] || colorMap.indigo;

  return (
    <div>
      <div className="flex items-center justify-between text-sm mb-1">
        <span className="text-slate-400">{label}</span>
        <div className="flex items-center gap-3">
          <span className={`font-medium ${c.text}`}>{fmtUSD(value)}</span>
          <span className="text-slate-500 text-xs w-14 text-right">{fmt(pct, 1)}%</span>
        </div>
      </div>
      <div className="h-1.5 bg-slate-700 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all ${c.bar}`}
          style={{ width: `${Math.min(pct, 100)}%` }}
        />
      </div>
    </div>
  );
};

export default RiskDashboard;
